/* eslint-disable camelcase */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Select } from 'antd';
import { Plus, Trash2, X } from 'feather-icons-react';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';

import { Button, Checkbox, Field, IconButton, Indicator, Input, Modal } from 'components/common';

import {
  ConversionRule,
  TriggerConfig,
  TriggerType,
  ExtractorType,
  FrequencyType,
  CONVERSION_CATEGORIES,
  TRIGGER_TYPES,
  FREQUENCY_OPTIONS,
  EXTRACTOR_TYPES,
} from 'modules/pixel/pixelApiTypes';

type ConversionRuleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rule: ConversionRule, originalIndex?: number) => Promise<void>;
  editingRule?: ConversionRule | null;
  editingIndex?: number;
};

type TriggerFormValue = {
  type: TriggerType;
  value: string;
};

type ExtractorFormValue = {
  enabled: boolean;
  type: ExtractorType;
  value: string;
};

type FormInputs = {
  name: string;
  category: string;
  subcategory: string;
  triggers: TriggerFormValue[];
  frequency: FrequencyType;
  extractors: {
    order_number: ExtractorFormValue;
    amount: ExtractorFormValue;
    currency: ExtractorFormValue;
  };
};

const getTriggerValue = (trigger: TriggerConfig): string => {
  if (trigger.type === 'url_match') return trigger.pattern || '';
  if (trigger.type === 'url_contains') return trigger.contains || '';
  if (trigger.type === 'click' || trigger.type === 'form_submit') return trigger.selector || '';

  return '';
};

const getTriggerPlaceholder = (type: TriggerType): string => {
  switch (type) {
    case 'url_match':
      return 'e.g., ^/book-a-demo/?$  (pathname only — no scheme/host)';
    case 'url_contains':
      return 'e.g., /thank-you  (substring on full URL)';
    case 'click':
      return 'e.g., #button-id';
    case 'form_submit':
      return 'e.g., #contact-form';
    default:
      return '';
  }
};

const DEFAULT_EXTRACTORS: FormInputs['extractors'] = {
  order_number: { enabled: false, type: 'urlParam', value: '' },
  amount: { enabled: false, type: 'selector', value: '' },
  currency: { enabled: false, type: 'static', value: 'USD' },
};

export const ConversionRuleModal = ({
  isOpen,
  onClose,
  onSave,
  editingRule,
  editingIndex,
}: ConversionRuleModalProps) => {
  const isEditing = editingRule !== null && editingRule !== undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      category: 'pagevisit',
      subcategory: '',
      triggers: [{ type: 'page_load', value: '' }],
      frequency: 'perPage',
      extractors: DEFAULT_EXTRACTORS,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'triggers',
  });

  const category = watch('category');
  const extractors = watch('extractors');

  // Reset form when modal opens/closes or editing rule changes
  useEffect(() => {
    if (isOpen) {
      if (editingRule) {
        const triggers: TriggerFormValue[] = editingRule.trigger?.length
          ? editingRule.trigger.map((t) => ({ type: t.type, value: getTriggerValue(t) }))
          : [{ type: 'page_load', value: '' }];

        const extractorValues: FormInputs['extractors'] = { ...DEFAULT_EXTRACTORS };

        if (editingRule.extractors) {
          (['order_number', 'amount', 'currency'] as const).forEach((key) => {
            if (editingRule.extractors?.[key]) {
              extractorValues[key] = {
                enabled: true,
                type: editingRule.extractors[key].type as ExtractorType,
                value: editingRule.extractors[key].value || '',
              };
            }
          });
        }

        reset({
          name: editingRule.name || '',
          category: editingRule.category || 'pagevisit',
          subcategory: editingRule.subcategory || '',
          triggers,
          frequency: (editingRule.frequency as FrequencyType) || 'perPage',
          extractors: extractorValues,
        });
      } else {
        reset({
          name: '',
          category: 'pagevisit',
          subcategory: '',
          triggers: [{ type: 'page_load', value: '' }],
          frequency: 'perPage',
          extractors: DEFAULT_EXTRACTORS,
        });
      }
    }
  }, [isOpen, editingRule, reset]);

  const handleFormSubmit = handleSubmit(async (data) => {
    // Validate name
    if (!data.name.trim()) {
      setSubmitError('Name is required');

      return;
    }

    // Validate triggers
    for (const trigger of data.triggers) {
      if (trigger.type !== 'page_load' && !trigger.value.trim()) {
        setSubmitError('Trigger value is required');

        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build triggers array
      const triggers: TriggerConfig[] = data.triggers.map((t) => {
        const trigger: TriggerConfig = { type: t.type };

        if (t.type === 'url_match') trigger.pattern = t.value;
        else if (t.type === 'url_contains') trigger.contains = t.value;
        else if (t.type === 'click' || t.type === 'form_submit') trigger.selector = t.value;

        return trigger;
      });

      // Build extractors object
      let extractorsObj: Record<string, { type: ExtractorType; value: string }> | undefined;

      if (data.category === 'checkout') {
        extractorsObj = {};
        (['order_number', 'amount', 'currency'] as const).forEach((key) => {
          if (data.extractors[key].enabled && data.extractors[key].value) {
            extractorsObj![key] = {
              type: data.extractors[key].type,
              value: data.extractors[key].value,
            };
          }
        });

        if (Object.keys(extractorsObj).length === 0) {
          extractorsObj = undefined;
        }
      }

      const rule: ConversionRule = {
        name: data.name.trim(),
        category: data.category,
        subcategory: data.subcategory.trim() || undefined,
        trigger: triggers,
        frequency: data.frequency,
        extractors: extractorsObj,
      };

      await onSave(rule, editingIndex);
      onClose();
    } catch (error) {
      setSubmitError('Failed to save conversion rule. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleCancel = () => {
    reset();
    onClose();
  };

  const addTrigger = () => {
    append({ type: 'page_load', value: '' });
  };

  return (
    <Modal open={isOpen} onCancel={handleCancel} footer={null} closable={false} destroyOnClose>
      <div className="w-[600px] bg-white rounded-xl flex-col justify-start items-start inline-flex">
        {/* Header */}
        <div className="self-stretch px-6 justify-between items-start gap-6 inline-flex">
          <div className="grow shrink basis-0 py-5 flex-col justify-start items-start gap-2 inline-flex">
            <h1 className="self-stretch text-base-md font-medium leading-snug text-secondary-black">
              {isEditing ? 'Edit Conversion Rule' : 'Add Conversion Rule'}
            </h1>
          </div>
          <IconButton
            variant="tertiary"
            className="bg-transparent hover:bg-primary-gray-25 mt-5"
            size="sm"
            Icon={X}
            onClick={handleCancel}
            disabled={isSubmitting}
          />
        </div>

        {/* Content */}
        <div className="px-6 pt-3 pb-6 gap-6 stretch-to-form w-full">
          <form className="w-full gap-4 flex flex-col justify-start items-start">
            {/* Name */}
            <Field
              label="Name"
              render={({ field, fieldState: { invalid } }) => (
                <Input {...field} invalid={invalid} placeholder="Enter conversion name" />
              )}
              control={control}
              name="name"
              error={errors.name?.message}
            />

            {/* Category */}
            <Field
              label="Category"
              render={({ field }) => (
                <Select
                  {...field}
                  className="w-full"
                  options={CONVERSION_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
                />
              )}
              control={control}
              name="category"
              error={errors.category?.message}
            />

            {/* Subcategory */}
            <Field
              label="Subcategory"
              isOptional
              render={({ field }) => <Input {...field} placeholder="e.g., homepage, contactus" />}
              control={control}
              name="subcategory"
            />

            {/* Triggers */}
            <div className="w-full flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-base-sm leading-tight font-medium text-secondary-black">
                  Triggers
                </label>
                <button
                  type="button"
                  onClick={addTrigger}
                  className="flex items-center gap-1 text-base-xs text-primary-teal-500 hover:text-primary-teal-600 transition-colors"
                >
                  <Plus size={14} />
                  Add Trigger
                </button>
              </div>

              {fields.map((field, index) => {
                const triggerType = watch(`triggers.${index}.type`);
                const triggerValue = watch(`triggers.${index}.value`) ?? '';
                const showFullUrlWarning =
                  triggerType === 'url_match' && triggerValue.includes('://');

                return (
                  <div key={field.id} className="flex items-start gap-2">
                    <div className="flex-1">
                      <Field
                        render={({ field: selectField }) => (
                          <Select
                            {...selectField}
                            className="w-full"
                            options={TRIGGER_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                            onChange={(value) => {
                              selectField.onChange(value);
                              setValue(`triggers.${index}.value`, '');
                            }}
                          />
                        )}
                        control={control}
                        name={`triggers.${index}.type`}
                      />
                    </div>

                    {triggerType !== 'page_load' && (
                      <div className="flex-1 flex flex-col gap-1">
                        <Field
                          render={({ field: inputField, fieldState: { invalid } }) => (
                            <Input
                              {...inputField}
                              invalid={invalid}
                              placeholder={getTriggerPlaceholder(triggerType)}
                              onBlur={(e) => {
                                const trimmed = e.target.value.trim();

                                if (trimmed !== e.target.value) {
                                  setValue(`triggers.${index}.value`, trimmed, {
                                    shouldValidate: true,
                                  });
                                }

                                inputField.onBlur();
                              }}
                            />
                          )}
                          control={control}
                          name={`triggers.${index}.value`}
                          error={errors.triggers?.[index]?.value?.message}
                        />
                        {!errors.triggers?.[index]?.value && showFullUrlWarning && (
                          <span className="text-base-xs text-amber-600 leading-4">
                            Enter a pathname only (e.g. <code>/book-a-demo</code>). The matcher
                            tests <code>window.location.pathname</code>, so <code>https://…</code>{' '}
                            prefixes are stripped on save.
                          </span>
                        )}
                      </div>
                    )}

                    {fields.length > 1 && (
                      <IconButton
                        variant="tertiary"
                        size="sm"
                        Icon={Trash2}
                        onClick={() => remove(index)}
                        className="mt-1"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Extractors (only for checkout) */}
            {category === 'checkout' && (
              <div className="w-full flex flex-col gap-3">
                <label className="text-base-sm leading-tight font-medium text-secondary-black">
                  Data Extractors
                </label>

                {(['order_number', 'amount', 'currency'] as const).map((key) => (
                  <div key={key} className="flex items-center gap-3">
                    <div className="min-w-[110px]">
                      <Checkbox
                        checked={extractors[key]?.enabled || false}
                        onChange={(e) =>
                          setValue(`extractors.${key}.enabled`, e.target.checked, {
                            shouldValidate: true,
                          })
                        }
                      >
                        <span className="text-base-sm font-medium capitalize">
                          {key.replace('_', ' ')}
                        </span>
                      </Checkbox>
                    </div>

                    <div className="flex-1">
                      <Field
                        render={({ field }) => (
                          <Select
                            {...field}
                            className="w-full"
                            disabled={!extractors[key]?.enabled}
                            options={EXTRACTOR_TYPES.map((t) => ({
                              value: t.value,
                              label: t.label,
                            }))}
                          />
                        )}
                        control={control}
                        name={`extractors.${key}.type`}
                      />
                    </div>

                    <div className="flex-1">
                      <Field
                        render={({ field }) => (
                          <Input
                            {...field}
                            disabled={!extractors[key]?.enabled}
                            placeholder={
                              extractors[key]?.type === 'urlParam'
                                ? 'order_id'
                                : extractors[key]?.type === 'selector'
                                  ? '#order-total'
                                  : 'USD'
                            }
                          />
                        )}
                        control={control}
                        name={`extractors.${key}.value`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Frequency */}
            <Field
              label="Trigger Frequency"
              render={({ field }) => (
                <Select
                  {...field}
                  className="w-full"
                  options={FREQUENCY_OPTIONS.map((f) => ({ value: f.value, label: f.label }))}
                />
              )}
              control={control}
              name="frequency"
            />

            {/* Error indicator */}
            {submitError && (
              <Indicator variant="danger" size="lg">
                {submitError}
              </Indicator>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="self-stretch h-16 px-6 border-t border-primary-gray-100 justify-end items-center gap-2 inline-flex">
          <div className="justify-end items-center gap-2 flex">
            <Button
              size="md"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="w-20"
            >
              Cancel
            </Button>
            <Button
              size="md"
              variant="primary"
              disabled={isSubmitting}
              onClick={handleFormSubmit}
              isLoading={isSubmitting}
            >
              {isEditing ? 'Save changes' : 'Add Rule'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
